import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import { ErrorNotice } from '../components/ui/States';
import logo from '../assets/divert-logo-dark.svg';

const BAD_CREDENTIALS = 'Email or password is incorrect.';
const DEACTIVATED = 'This account has been deactivated — contact a super admin.';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(/deactivat/i.test(message) ? DEACTIVATED : BAD_CREDENTIALS);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] bg-paper">
      <div className="flex flex-col justify-between gap-10 px-6 py-8 md:px-16 md:py-14">
        <img src={logo} alt="DIVERT Mental Health" className="h-[40px] w-auto self-start" />

        <div className="min-w-0">
          <h2 className="font-brand text-ink">
            <span
              className="block font-extrabold text-[60px] leading-[64px] md:text-[84px] md:leading-[88px] tracking-[-0.02em]"
              style={{ fontVariationSettings: "'SOFT' 100, 'opsz' 144" }}
            >
              DIVERT{' '}
              <span className="text-accent italic font-normal" style={{ fontVariationSettings: "'opsz' 144" }}>
                360
              </span>
            </span>
            <span
              className="block font-light text-[30px] leading-9 md:text-[38px] md:leading-[46px] text-ink-2 mt-1"
              style={{ fontVariationSettings: "'opsz' 72" }}
            >
              Platform
            </span>
          </h2>
          <div aria-hidden className="w-12 h-[3px] rounded-full bg-accent mt-7 mb-5" />
          <p className="text-body text-ink-2 max-w-[36ch]">Members, events, programs and payments in one place.</p>
        </div>

        <div aria-hidden className="max-md:hidden" />
      </div>

      <div className="bg-surface flex flex-col justify-center gap-7 px-6 py-10 md:px-16 md:py-14 md:border-l md:border-line max-md:border-t max-md:border-line">
        <div>
          <div className="font-semibold text-small text-ink-2">Operator sign-in</div>
          <div className="border-b border-line-strong my-3" />
          <h1 className="font-serif font-semibold text-display text-ink">Sign in</h1>
        </div>

        {error ? <ErrorNotice>{error}</ErrorNotice> : null}

        <form onSubmit={submit} className="flex flex-col gap-5">
          <div>
            <label htmlFor="email" className="font-semibold block text-small text-ink-2 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@divertmentalhealth.ca"
              className="w-full border border-line-strong px-3 py-[11px] text-table rounded-sm min-h-[44px] placeholder:text-ink-3"
            />
          </div>

          <div>
            <label htmlFor="password" className="font-semibold block text-small text-ink-2 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="w-full border border-line-strong px-3 py-[11px] text-table rounded-sm min-h-[44px] placeholder:text-ink-3"
            />
          </div>

          <Button type="submit" variant="primary" size="lg" block disabled={busy} trailing={<span aria-hidden>→</span>}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <div className="border-b border-line" />

        <div className="flex flex-col gap-2 text-small text-ink-2">
          <p>Dashboard access is for platform operators. Members sign in through their member apps.</p>
          <p>Accounts are provisioned by a super admin — there is no self-signup or self-serve password reset.</p>
        </div>
      </div>
    </div>
  );
}
